import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '@ld.consumer-portal/data-access';
import { Loan } from '@ld.consumer-portal/models';
import { LoanSelectionService } from '@ld.consumer-portal/utils';
import { ApplicationTypeService } from './application-type.service';

export type DocumentsNavigationResult =
  | { type: 'no-loans' }
  | { type: 'single-loan'; loanNumber: string }
  | { type: 'multiple-loans'; loans: Loan[] };

@Injectable({
  providedIn: 'root',
})
export class DocumentsNavigationService {
  private readonly router = inject(Router);
  private readonly dashboardService = inject(DashboardService);
  private readonly loanSelectionService = inject(LoanSelectionService);
  private readonly applicationTypeService = inject(ApplicationTypeService);

  readonly showLoanSelectionModal = signal(false);

  readonly showNoLoansModal = signal(false);

  readonly eligibleLoans = signal<Loan[]>([]);

  getDocumentsNavigationResult(): DocumentsNavigationResult {
    const loanResponses = this.dashboardService.loanResponses();

    if (loanResponses.length === 0) {
      return { type: 'no-loans' };
    }

    if (loanResponses.length === 1) {
      return { type: 'single-loan', loanNumber: loanResponses[0].loan.loanNumber };
    }

    return { type: 'multiple-loans', loans: loanResponses.map((r) => r.loan) };
  }

  navigateToDocuments(): void {
    const result = this.getDocumentsNavigationResult();

    switch (result.type) {
      case 'no-loans':
        this.showNoLoansModal.set(true);
        break;

      case 'single-loan':
        this.router.navigate(['/loans', result.loanNumber, 'documents']);
        break;

      case 'multiple-loans': {
        const selectedLoanNumber = this.loanSelectionService.selectedLoanNumber();
        if (selectedLoanNumber) {
          const exists = result.loans.some((loan) => loan.loanNumber === selectedLoanNumber);
          if (exists) {
            this.router.navigate(['/loans', selectedLoanNumber, 'documents']);
            return;
          }
        }
        this.eligibleLoans.set(result.loans);
        this.showLoanSelectionModal.set(true);
        break;
      }
    }
  }

  onLoanSelected(loanNumber: string): void {
    this.closeLoanSelectionModal();
    this.router.navigate(['/loans', loanNumber, 'documents']);
  }

  closeLoanSelectionModal(): void {
    this.showLoanSelectionModal.set(false);
    this.eligibleLoans.set([]);
  }

  closeNoLoansModal(): void {
    this.showNoLoansModal.set(false);
  }

  onLinkLoan(): void {
    this.closeNoLoansModal();
    this.router.navigate(['/dashboard']);
  }

  onStartApplication(): void {
    this.closeNoLoansModal();
    this.applicationTypeService.openApplicationTypeSelector();
  }
}
